import type { Plugin, PluginContext } from '../core/types'
import { onNavigation } from '../shared/navigation'
import { uuid } from '../shared/uuid'
import { trackClick } from './clicks'

export interface WebAnalyticsOptions {
  autoPageviews?: boolean
  outboundLinks?: boolean
  fileDownloads?: boolean | string[]
  dataAttributes?: boolean
}

export interface WebAnalytics extends Plugin<'webAnalytics'> {
  page: (options?: { notFound?: boolean }) => void
}

interface Page {
  url: string
  title: string
  pageId: string
  path: string
}

interface Pending {
  url: string
  referrer: string | undefined
  timer: ReturnType<typeof setTimeout>
}

export function webAnalytics(options: WebAnalyticsOptions = {}): WebAnalytics {
  const cleanups: (() => void)[] = []
  let ctx: PluginContext | undefined
  let page: Page | undefined
  let pending: Pending | undefined
  let started = false
  let engagedMs = 0
  let engagedSince: number | undefined
  let maxScroll = 0
  let scrollQueued = false
  let resolveFirst: (() => void) | undefined

  const listen = (
    target: EventTarget,
    type: string,
    handler: (event: Event) => void,
    listenOptions?: AddEventListenerOptions,
  ) => {
    target.addEventListener(type, handler, listenOptions)
    cleanups.push(() => {
      target.removeEventListener(type, handler, listenOptions)
    })
  }

  const pathOf = (href: string) => {
    const url = new URL(href)
    const hash = ctx?.config.hashRouting ? (url.hash.split('?')[0] ?? '') : ''
    return url.pathname + hash
  }

  const updateEngagement = () => {
    const now = Date.now()
    if (engagedSince !== undefined) {
      engagedMs += now - engagedSince
      engagedSince = undefined
    }
    if (document.visibilityState === 'visible' && document.hasFocus()) {
      engagedSince = now
    }
  }

  const measureScroll = () => {
    const height = document.documentElement.scrollHeight
    const depth =
      height > 0
        ? Math.min(100, Math.round(((scrollY + innerHeight) / height) * 100))
        : 100
    maxScroll = Math.max(maxScroll, depth)
  }

  const sendLeave = () => {
    if (!page || !ctx) {
      return
    }
    updateEngagement()
    measureScroll()
    ctx.send(
      {
        name: '$pageleave',
        url: page.url,
        title: page.title,
        pageId: page.pageId,
        engagementMs: engagedMs,
        scrollDepth: maxScroll,
      },
      { beacon: true },
    )
    engagedMs = 0
  }

  const sendPageview = (
    url: string,
    referrer: string | undefined,
    notFound: boolean,
  ) => {
    if (!ctx) {
      return
    }
    page = { url, title: document.title, pageId: uuid(), path: pathOf(url) }
    engagedMs = 0
    engagedSince = undefined
    maxScroll = 0
    updateEngagement()
    measureScroll()
    const missing =
      notFound || document.querySelector('[data-loomduct-not-found]') !== null
    ctx.send({
      name: missing ? '$not_found' : '$pageview',
      url,
      pageId: page.pageId,
      ...(referrer !== undefined && { referrer }),
    })
    resolveFirst?.()
    resolveFirst = undefined
  }

  const flushPending = () => {
    if (!pending) {
      return
    }
    clearTimeout(pending.timer)
    const { url, referrer } = pending
    pending = undefined
    sendPageview(url, referrer, false)
  }

  const schedule = (referrer: string | undefined) => {
    flushPending()
    pending = {
      url: location.href,
      referrer,
      timer: setTimeout(flushPending, 100),
    }
  }

  const onNavigate = (kind: 'push' | 'replace' | 'pop' | 'hash') => {
    const href = location.href
    if (pending && kind === 'replace') {
      pending.url = href
      return
    }
    flushPending()
    if (kind === 'hash' && !ctx?.config.hashRouting) {
      return
    }
    if (page && pathOf(href) === page.path) {
      return
    }
    const previous = page?.url
    sendLeave()
    schedule(previous)
  }

  const tryBegin = () => {
    if (
      started ||
      document.readyState === 'loading' ||
      document.visibilityState !== 'visible'
    ) {
      return
    }
    started = true
    if (!page && !pending) {
      schedule(undefined)
    }
  }

  const onVisibilityChange = () => {
    updateEngagement()
    if (document.visibilityState === 'hidden') {
      flushPending()
      sendLeave()
    } else if (options.autoPageviews !== false) {
      tryBegin()
    }
  }

  const onPageHide = () => {
    if (document.visibilityState !== 'hidden') {
      flushPending()
      sendLeave()
    }
  }

  const onPageShow = (event: Event) => {
    if (
      (event as PageTransitionEvent).persisted &&
      options.autoPageviews !== false
    ) {
      page = undefined
      schedule(undefined)
    }
  }

  const onScroll = () => {
    if (scrollQueued) {
      return
    }
    scrollQueued = true
    requestAnimationFrame(() => {
      scrollQueued = false
      measureScroll()
    })
  }

  return {
    name: 'webAnalytics',
    init: (context) => {
      ctx = context
    },
    load: () => {
      listen(document, 'visibilitychange', onVisibilityChange)
      listen(window, 'pagehide', onPageHide)
      listen(window, 'pageshow', onPageShow)
      listen(window, 'focus', updateEngagement)
      listen(window, 'blur', updateEngagement)
      listen(window, 'scroll', onScroll, { passive: true })
      if (
        options.outboundLinks ||
        options.fileDownloads ||
        options.dataAttributes
      ) {
        const onClick = (event: Event) => {
          if (ctx) {
            trackClick(event as MouseEvent, ctx, options, page?.pageId)
          }
        }
        listen(document, 'click', onClick, { capture: true, passive: true })
        listen(document, 'auxclick', onClick, { capture: true, passive: true })
      }
      if (options.autoPageviews === false) {
        return undefined
      }
      cleanups.push(onNavigation(onNavigate))
      listen(document, 'DOMContentLoaded', tryBegin)
      const first = new Promise<void>((resolve) => {
        resolveFirst = resolve
      })
      tryBegin()
      return first
    },
    flush: () => {
      flushPending()
      sendLeave()
    },
    destroy: () => {
      if (pending) {
        clearTimeout(pending.timer)
      }
      pending = undefined
      for (const cleanup of cleanups.splice(0)) {
        cleanup()
      }
    },
    page: (pageOptions) => {
      if (typeof document === 'undefined') {
        return
      }
      flushPending()
      const previous = page?.url
      sendLeave()
      sendPageview(location.href, previous, pageOptions?.notFound === true)
    },
  }
}
